// THE APP'S SEVEN STEPS, IN ITS ORDER AND UNDER ITS NAMES (WEB-PARITY-5, SW-2 and SW-3; ruling D3).
//
// The app's progress bar names seven phases — Details, Condition, Comps, Analyze, Value, Letter, Review — and reads
// "Step n of 7" (`AppealPhase`, AppealAce/Features/Flow/AppealProgressBar.swift:9-21, :86). The website's rail is the
// same seven, each at the route that IS that step's screen:
//   1 Details   /details    SubjectPropertyView (WEB-PARITY-1C, 2A)
//   2 Condition /condition  PropertyConditionView (WEB-PARITY-3)
//   3 Comps     /comps      CompsView (WEB-PARITY-3)
//   4 Analyze   /analyze    AnalyzeView (WEB-PARITY-4)
//   5 Value     /verdict    ValueView (WEB-PARITY-4)
//   6 Letter    /you        GenerateView (WEB-PARITY-5)
//   7 Review    /letter     ReviewView (WEB-PARITY-6)
// Until this batch the rail was the prototype's eight ("Your property" … "Narrow the comps", "The verdict", "Your
// details", "Building", "Review and file"). "Building" was never a step: the app draws it as a full-screen cover over
// step 6 (`LetterGenerationOverlay`), so `/building` is that cover — a route, so a reload or a shared link finds the
// letter being written, and not a step the rail counts.
//
// Each label is the app's own word, pinned to its line by `test/parity5.test.tsx`.
//
// THESE ARE LABELS AND PATHS. Nothing here decides what an owner may reach: that is the entitlement the server answers
// on every response, read by `PaidRoute`.

import type { AppealSnapshot } from "@contract";

const APB = "Features/Flow/AppealProgressBar.swift";

export const STEPS = [
  { path: "/details", label: "Details", appSource: `${APB}:14` },
  { path: "/condition", label: "Condition", appSource: `${APB}:15` },
  { path: "/comps", label: "Comps", appSource: `${APB}:16` },
  { path: "/analyze", label: "Analyze", appSource: `${APB}:17` },
  { path: "/verdict", label: "Value", appSource: `${APB}:18` },
  { path: "/you", label: "Letter", appSource: `${APB}:19` },
  { path: "/letter", label: "Review", appSource: `${APB}:20` },
] as const;

export type StepRoute = (typeof STEPS)[number]["path"];

/** The app's generation overlay (`LetterGenerationOverlay`), drawn over step 6. A paid route, not a step. */
export const OVERLAY_ROUTE = "/building";

/** Every route the workspace draws: the seven steps and the overlay over step 6. */
export type WorkspaceRoute = StepRoute | typeof OVERLAY_ROUTE;

/** The step a workspace route sits on, for the rail: the overlay covers step 6, "Letter". */
export function stepOf(route: WorkspaceRoute): StepRoute {
  return route === OVERLAY_ROUTE ? "/you" : route;
}

/** Every step is behind the spend, and so is the overlay. The gates and checkout are not. */
export const PAID_ROUTES: readonly string[] = [...STEPS.map((s) => s.path), OVERLAY_ROUTE];

// ── WHAT THE SERVER'S SNAPSHOT SAYS THIS APPEAL HAS REACHED (WALK-FIX-1, R236) ─────────────────────
//
// The rail links every step up to the one on screen. After "Revise from your details" sends the owner back to step 1,
// that left only Details live, and Tyler, 2026-10-05, was "stuck back at the beginning" with a finished letter. So the
// rail also links every step the appeal has REACHED, read off the snapshot every response carries, never kept here:
//   - a shipped letter (`letter.status`, with the generation it names) has reached Review, step 7;
//   - a letter queued at all (`letter.latestGenerationAt`) has reached Letter, step 6;
//   - otherwise nothing beyond the step on screen. NOT `requestedValue`: the server sets it when `v2Value` first
//     answers, which can be before the owner has seen the Value step, and a link must never skip a step they have not
//     been through.
// Whether the owner may open a step at all stays `PaidRoute`'s answer (the entitlement), as before.

/** The index in `STEPS` of the furthest step the snapshot says this appeal has reached, or -1 for none it can name. */
export function reachedStep(appeal: AppealSnapshot | null): number {
  if (appeal === null) return -1;
  if (appeal.letter.status === "shipped" && appeal.letter.latestGenerationAt !== null) return STEPS.length - 1;
  if (appeal.letter.latestGenerationAt !== null) return STEPS.length - 2;
  return -1;
}

/** Where a rail link goes. Review names the generation it shows: `/letter` with none fetches nothing the server has. */
export function stepHref(path: StepRoute, appeal: AppealSnapshot | null): string {
  if (path !== "/letter" || appeal === null || appeal.letter.latestGenerationAt === null) return path;
  return `/letter?generationAt=${appeal.letter.latestGenerationAt}`;
}
